/**
 * ArcGIS Pro–style raster symbology modes and color-ramp previews.
 *
 * Render modes drive which TiTiler params the Raster ToolCard applies.
 * Visual CSS gradients are for gallery UI only — they never touch the map.
 *
 * Mode set mirrors ArcGIS Pro Primary Symbology (Stretch, Discrete, Classify,
 * Unique Values, Shaded Relief, Vector Field) plus RGB composite and a
 * dedicated grayscale Hillshade. Legacy QGIS ids (`singleband-gray`,
 * `singleband-pseudocolor`, `contours`) still type-check as aliases so older
 * saved state / tests keep compiling; {@link normalizeRasterRenderMode} maps
 * them onto the ArcGIS set before apply.
 */

import type { RasterColorRamp, RasterToolCardState } from '@/state/useViewerStore';

/** ArcGIS Pro–style primary symbology + RGB + grayscale hillshade. */
export type RasterRenderMode =
  | 'multiband-color'
  | 'stretch'
  | 'discrete'
  | 'classify'
  | 'unique-values'
  | 'shaded-relief'
  | 'hillshade'
  | 'vector-field'
  /** @deprecated Prefer `stretch` with greyscale ramp. */
  | 'singleband-gray'
  /** @deprecated Prefer `stretch`. */
  | 'singleband-pseudocolor'
  /** @deprecated Prefer `discrete`. */
  | 'contours';

export const RASTER_RENDER_MODES: readonly {
  id: RasterRenderMode;
  label: string;
  description: string;
}[] = [
  {
    id: 'multiband-color',
    label: 'RGB composite',
    description: 'True-colour composite from three bands (ArcGIS RGB).',
  },
  {
    id: 'stretch',
    label: 'Stretch',
    description: 'Stretches values along a color ramp (ArcGIS Stretch).',
  },
  {
    id: 'discrete',
    label: 'Discrete',
    description: 'Groups data into a fixed number of colors from a scheme.',
  },
  {
    id: 'classify',
    label: 'Classify',
    description: 'Assigns a color for each class of values (editable breaks).',
  },
  {
    id: 'unique-values',
    label: 'Unique Values',
    description: 'Assigns a color for each distinct value (categorical).',
  },
  {
    id: 'shaded-relief',
    label: 'Shaded Relief',
    description: 'Elevation color ramp merged with hillshade lighting.',
  },
  {
    id: 'hillshade',
    label: 'Hillshade',
    description: 'Grayscale shaded relief (gdaldem / QGIS hillshade).',
  },
  {
    id: 'vector-field',
    label: 'Vector Field',
    description: 'Displays magnitude/direction as vector symbols (coming soon).',
  },
];

export interface RasterColorRampPreset {
  readonly id: RasterColorRamp;
  readonly label: string;
  /** CSS linear-gradient stops for the gallery preview bar. */
  readonly cssGradient: string;
  /** TiTiler `colormap_name`. */
  readonly titilerName: string;
}

/** Continuous ramps used by Stretch / Classify / Discrete / Shaded Relief. */
export const RASTER_COLOR_RAMP_PRESETS: readonly RasterColorRampPreset[] = [
  {
    id: 'arcgis-elevation',
    label: 'ArcGIS Elevation',
    cssGradient:
      'linear-gradient(90deg, #C8FAED 0%, #e2fa89 12.5%, #00a526 25%, #D4A700 37.5%, #ba0e02 50%, #a10c02 62.5%, #6E390A 75%, #9e9191 87.5%, #ded5d5 100%)',
    // Shaded Relief expands this id to a custom TiTiler colormap. Keeping the
    // id here lets the compact gv_sr_cmap marker survive engine URL rewrites.
    titilerName: 'arcgis-elevation',
  },
  {
    id: 'spectral',
    label: 'Spectral',
    cssGradient:
      'linear-gradient(90deg, #9e0142 0%, #f46d43 20%, #fee08b 40%, #e6f598 55%, #66c2a5 70%, #3288bd 85%, #5e4fa2 100%)',
    // Custom smooth map for Shaded Relief (same path as ArcGIS Elevation).
    titilerName: 'spectral',
  },
  {
    id: 'viridis',
    label: 'Viridis',
    cssGradient:
      'linear-gradient(90deg, #440154, #414487, #2a788e, #22a884, #7ad151, #fde725)',
    titilerName: 'viridis',
  },
  {
    id: 'magma',
    label: 'Magma',
    cssGradient:
      'linear-gradient(90deg, #000004, #3b0f70, #8c2981, #de4968, #fe9f6d, #fcfdbf)',
    titilerName: 'magma',
  },
  {
    id: 'inferno',
    label: 'Inferno',
    cssGradient:
      'linear-gradient(90deg, #000004, #420a68, #932667, #dd513a, #fca50a, #fcffa4)',
    titilerName: 'inferno',
  },
  {
    id: 'plasma',
    label: 'Plasma',
    cssGradient:
      'linear-gradient(90deg, #0d0887, #6a00a8, #b12a90, #e16462, #fca636, #f0f921)',
    titilerName: 'plasma',
  },
  {
    id: 'turbo',
    label: 'Turbo',
    cssGradient:
      'linear-gradient(90deg, #30123b, #466be3, #28bceb, #7cfa70, #efd930, #d93806)',
    titilerName: 'turbo',
  },
  {
    id: 'terrain',
    label: 'Terrain',
    cssGradient:
      'linear-gradient(90deg, #333399, #00ffff, #00ff00, #ffff00, #ff7f00, #7f0000)',
    titilerName: 'terrain',
  },
  {
    id: 'RdBu',
    label: 'RdBu',
    cssGradient:
      'linear-gradient(90deg, #67001f, #f4a582, #f7f7f7, #92c5de, #053061)',
    titilerName: 'rdBu',
  },
  {
    id: 'greyscale',
    label: 'Greyscale',
    cssGradient: 'linear-gradient(90deg, #000000, #808080, #ffffff)',
    titilerName: 'gray',
  },
];

/** Discrete blocks for Unique values preview when no table is loaded yet. */
export const UNIQUE_VALUES_PREVIEW_GRADIENT =
  'linear-gradient(90deg, #7fc97f 0% 10%, #beaed4 10% 20%, #fdc086 20% 30%, #ffff99 30% 40%, #386cb0 40% 50%, #f0027f 50% 60%, #bf5b17 60% 70%, #666666 70% 80%, #1b9e77 80% 90%, #d95f02 90% 100%)';

export function getRampPreset(id: RasterColorRamp): RasterColorRampPreset {
  return (
    RASTER_COLOR_RAMP_PRESETS.find((r) => r.id === id) ?? RASTER_COLOR_RAMP_PRESETS[0]
  );
}

/** Map legacy QGIS mode ids onto the ArcGIS Pro set. */
export function normalizeRasterRenderMode(
  mode: RasterRenderMode | undefined,
): RasterRenderMode | undefined {
  if (mode == null) return undefined;
  switch (mode) {
    case 'singleband-gray':
    case 'singleband-pseudocolor':
      return 'stretch';
    case 'contours':
      return 'discrete';
    default:
      return mode;
  }
}

/**
 * Store patch applied when the operator picks a render mode.
 * Keeps bands / ramp consistent with ArcGIS defaults.
 */
export function patchForRasterRenderMode(
  mode: RasterRenderMode,
  previous: Pick<RasterToolCardState, 'bands' | 'colorRamp'>,
): Partial<RasterToolCardState> {
  const normalized = normalizeRasterRenderMode(mode);
  switch (normalized) {
    case 'multiband-color':
      return {
        renderMode: normalized,
        bands: [1, 2, 3],
      };
    case 'stretch':
      return {
        renderMode: normalized,
        bands: [previous.bands[0], previous.bands[0], previous.bands[0]],
        colorRamp:
          mode === 'singleband-gray'
            ? 'greyscale'
            : previous.colorRamp === 'greyscale'
              ? 'viridis'
              : previous.colorRamp,
      };
    case 'discrete':
      return {
        renderMode: normalized,
        bands: [1, 1, 1],
        colorRamp:
          previous.colorRamp === 'greyscale' ? 'terrain' : previous.colorRamp,
        discreteColorCount: 10,
      };
    case 'classify':
      return {
        renderMode: normalized,
        bands: [1, 1, 1],
        colorRamp:
          previous.colorRamp === 'greyscale' ? 'terrain' : previous.colorRamp,
        classCount: 5,
        classifyMethod: 'equal-interval',
      };
    case 'unique-values':
      return {
        renderMode: normalized,
        bands: [1, 1, 1],
        colorRamp: previous.colorRamp === 'greyscale' ? 'turbo' : previous.colorRamp,
      };
    case 'shaded-relief':
      return {
        renderMode: normalized,
        bands: [1, 1, 1],
        // ArcGIS Pro's elevation relief scheme: cyan/green low terrain,
        // yellow/orange/red upper terrain, then pale gray/white summits.
        colorRamp: 'arcgis-elevation',
      };
    case 'hillshade':
      return {
        renderMode: normalized,
        bands: [1, 1, 1],
        colorRamp: 'greyscale',
      };
    case 'vector-field':
      return {
        renderMode: normalized,
        bands: previous.bands,
      };
    default:
      return { renderMode: normalized };
  }
}

/**
 * Infer a render mode from the live TiTiler tile template.
 */
export function inferRasterRenderModeFromTemplate(
  tilesUrl: string,
  bandCount?: number,
): RasterRenderMode | undefined {
  const algorithm = /[?&]algorithm=([^&]*)/.exec(tilesUrl)?.[1];
  if (algorithm && decodeURIComponent(algorithm).toLowerCase() === 'hillshade') {
    // Shaded relief parks a marker so we can tell it apart from plain hillshade.
    if (/[?&]gv_shaded_relief=1(?:&|$)/.test(tilesUrl)) return 'shaded-relief';
    return 'hillshade';
  }
  if (/[?&]gv_render=classify(?:&|$)/.test(tilesUrl)) return 'classify';
  if (/[?&]gv_render=discrete(?:&|$)/.test(tilesUrl)) return 'discrete';
  if (/[?&]gv_render=unique(?:&|$)/.test(tilesUrl)) return 'unique-values';
  if (/[?&]colormap=/.test(tilesUrl) && !/[?&]colormap_name=/.test(tilesUrl)) {
    return 'classify';
  }

  const bidxMatches = tilesUrl.match(/[?&]bidx=([^&]*)/g) ?? [];
  const bands = bidxMatches
    .map((m) => Number(m.split('=')[1]))
    .filter((n) => Number.isFinite(n));
  const colormap = /[?&]colormap_name=([^&]*)/
    .exec(tilesUrl)?.[1]
    ?.toLowerCase();

  if (bands.length >= 3 && new Set(bands.slice(0, 3)).size === 3) {
    return 'multiband-color';
  }
  if (typeof bandCount === 'number' && bandCount >= 3 && bands.length === 0 && !colormap) {
    return 'multiband-color';
  }
  if (colormap === 'gray' || colormap === 'greyscale' || colormap === 'grey') {
    return 'stretch';
  }
  if (colormap) {
    return 'stretch';
  }
  if (bands.length === 1 || (typeof bandCount === 'number' && bandCount === 1)) {
    return 'stretch';
  }
  return undefined;
}
